import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Select,
  Textarea,
  Button,
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tag,
  Wrap,
  WrapItem,
  useToast,
  Code,
  Icon
} from '@chakra-ui/react';
import { 
  FiMail, 
  FiUser, 
  FiCalendar, 
  FiClock,
  FiPaperclip,
  FiTag
} from 'react-icons/fi';

const EmailDetailsModal = ({ isOpen, onClose, email, onStatusUpdate }) => {
  const [status, setStatus] = useState(email?.responseStatus?.status || 'pending');
  const [notes, setNotes] = useState(email?.responseStatus?.notes || '');
  const [updating, setUpdating] = useState(false);
  const toast = useToast();

  if (!email) return null;

  const handleStatusUpdate = async () => {
    setUpdating(true);
    try {
      await onStatusUpdate(email._id, status, notes);
      toast({
        title: 'Status atualizado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: 'Erro ao atualizar',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatResponseTime = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'replied': return 'green';
      case 'ignored': return 'gray';
      case 'in_progress': return 'blue';
      default: return 'gray';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'red';
      case 'high': return 'orange';
      case 'normal': return 'blue';
      case 'low': return 'gray';
      default: return 'gray';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Detalhes do Email Encaminhado</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Tabs colorScheme="blue">
            <TabList>
              <Tab>Informações Gerais</Tab>
              <Tab>Conteúdo do Email</Tab>
              <Tab>Status e Acompanhamento</Tab>
            </TabList>

            <TabPanels>
              {/* General Information Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {/* Original Email Info */}
                  <Box>
                    <Text fontWeight="bold" mb={2}>Email Original</Text>
                    <VStack align="stretch" spacing={2} pl={4}>
                      <HStack>
                        <Icon as={FiUser} />
                        <Text fontWeight="semibold">De:</Text>
                        <Text>
                          {email.originalEmail.fromName || email.originalEmail.from}
                          {email.originalEmail.fromName && (
                            <Text as="span" color="gray.500" ml={2}>
                              ({email.originalEmail.from})
                            </Text>
                          )}
                        </Text>
                      </HStack>
                      
                      <HStack>
                        <Icon as={FiMail} />
                        <Text fontWeight="semibold">Para:</Text>
                        <Text>{email.originalEmail.to}</Text>
                      </HStack>
                      
                      <HStack>
                        <Icon as={FiCalendar} />
                        <Text fontWeight="semibold">Recebido em:</Text>
                        <Text>{formatDate(email.originalEmail.receivedAt)}</Text>
                      </HStack>
                      
                      {email.originalEmail.attachmentCount > 0 && (
                        <HStack>
                          <Icon as={FiPaperclip} />
                          <Text fontWeight="semibold">Anexos:</Text>
                          <Badge colorScheme="blue">
                            {email.originalEmail.attachmentCount}
                          </Badge>
                        </HStack>
                      )}
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Forwarding Info */}
                  <Box>
                    <Text fontWeight="bold" mb={2}>Informações de Encaminhamento</Text>
                    <VStack align="stretch" spacing={2} pl={4}>
                      <HStack>
                        <Icon as={FiUser} />
                        <Text fontWeight="semibold">Encaminhado para:</Text>
                        <Text>
                          {email.forwardedTo.userId?.name || email.forwardedTo.name}
                          <Text as="span" color="gray.500" ml={2}>
                            ({email.forwardedTo.email})
                          </Text>
                        </Text>
                      </HStack>
                      
                      <HStack>
                        <Icon as={FiCalendar} />
                        <Text fontWeight="semibold">Encaminhado em:</Text>
                        <Text>{formatDate(email.forwardingDetails.forwardedAt)}</Text>
                      </HStack>
                      
                      <HStack>
                        <Icon as={FiClock} />
                        <Text fontWeight="semibold">Tempo de processamento:</Text>
                        <Text>{email.forwardingDetails.processingTime}ms</Text>
                      </HStack>
                      
                      <HStack>
                        <Icon as={FiTag} />
                        <Text fontWeight="semibold">Prioridade:</Text>
                        <Badge colorScheme={getPriorityColor(email.metadata.priority)}>
                          {email.metadata.priority}
                        </Badge>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Metadata */}
                  {email.metadata.tags && email.metadata.tags.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Text fontWeight="bold" mb={2}>Tags</Text>
                        <Wrap pl={4}>
                          {email.metadata.tags.map((tag, index) => (
                            <WrapItem key={index}>
                              <Tag colorScheme="blue">{tag}</Tag>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    </>
                  )}
                </VStack>
              </TabPanel>

              {/* Email Content Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="bold" mb={2}>Assunto</Text>
                    <Text pl={4}>{email.originalEmail.subject}</Text>
                  </Box>

                  <Divider />

                  {email.originalEmail.textPreview && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Conteúdo (Texto)</Text>
                      <Box 
                        pl={4} 
                        p={3} 
                        bg="gray.50" 
                        borderRadius="md"
                        maxH="300px"
                        overflowY="auto"
                      >
                        <Text whiteSpace="pre-wrap" fontSize="sm">
                          {email.originalEmail.fullText || email.originalEmail.textPreview}
                        </Text>
                      </Box>
                    </Box>
                  )}

                  {email.originalEmail.htmlPreview && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Conteúdo (HTML)</Text>
                      <Box 
                        pl={4} 
                        p={3} 
                        bg="gray.50" 
                        borderRadius="md"
                        maxH="300px"
                        overflowY="auto"
                      >
                        <div 
                          dangerouslySetInnerHTML={{ 
                            __html: email.originalEmail.fullHtml || email.originalEmail.htmlPreview 
                          }}
                          style={{ fontSize: '14px' }}
                        />
                      </Box>
                    </Box>
                  )}

                  {email.originalEmail.attachments && email.originalEmail.attachments.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Text fontWeight="bold" mb={2}>Anexos</Text>
                        <VStack align="stretch" pl={4} spacing={1}>
                          {email.originalEmail.attachments.map((att, index) => (
                            <HStack key={index}>
                              <Icon as={FiPaperclip} />
                              <Text fontSize="sm">{att.filename}</Text>
                              <Text fontSize="sm" color="gray.500">
                                ({att.contentType}, {(att.size / 1024).toFixed(2)} KB)
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    </>
                  )}
                </VStack>
              </TabPanel>

              {/* Status Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="bold" mb={2}>Status Atual</Text>
                    <HStack pl={4}>
                      <Badge 
                        colorScheme={getStatusColor(email.responseStatus.status)}
                        fontSize="md"
                        p={2}
                      >
                        {email.responseStatus.status}
                      </Badge>
                      {email.responseStatus.repliedAt && (
                        <Text fontSize="sm" color="gray.500">
                          Respondido em: {formatDate(email.responseStatus.repliedAt)}
                        </Text>
                      )}
                    </HStack>
                  </Box>

                  {email.responseStatus.responseTime && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Tempo de Resposta</Text>
                      <Text pl={4}>
                        {formatResponseTime(email.responseStatus.responseTime)}
                      </Text>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Text fontWeight="bold" mb={2}>Atualizar Status</Text>
                    <VStack align="stretch" pl={4} spacing={3}>
                      <Select 
                        value={status} 
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="pending">Pendente</option>
                        <option value="in_progress">Em Progresso</option>
                        <option value="replied">Respondido</option>
                        <option value="ignored">Ignorado</option>
                      </Select>
                      
                      <Textarea
                        placeholder="Adicionar notas ou observações..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                      />
                    </VStack>
                  </Box>

                  {email.responseStatus.updatedBy && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Última Atualização</Text>
                      <Text pl={4} fontSize="sm" color="gray.600">
                        Por {email.responseStatus.updatedBy.userName} em{' '}
                        {formatDate(email.responseStatus.updatedBy.updatedAt)}
                      </Text>
                    </Box>
                  )}

                  {email.responseStatus.notes && (
                    <Box>
                      <Text fontWeight="bold" mb={2}>Notas Anteriores</Text>
                      <Box pl={4} p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="sm">{email.responseStatus.notes}</Text>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleStatusUpdate}
            isLoading={updating}
          >
            Salvar Alterações
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmailDetailsModal;